import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Select, Input, Tag, Tooltip, Segmented, Button, ConfigProvider, DatePicker, message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CREATE_HUB_TASK } from '../graphql/mutations/workbenchMutations';

dayjs.extend(utc);
dayjs.extend(timezone);

interface WarehouseOption {
  warehouseId: number;
  warehouseName: string;
  /** 仓自己的 IANA 时区。同 hub 下 LA 与 LA_DC 可以不同，故 tz 挂到仓上而非片区上 */
  timeZone?: string | null;
}

interface AssignTaskPanelProps {
  hubId: number;
  warehouses: WarehouseOption[];
  recentTitles: string[];
  onAssigned?: () => void;
}

const AssignTaskPanel: React.FC<AssignTaskPanelProps> = ({
  hubId,
  warehouses,
  recentTitles,
  onAssigned,
}) => {
  const { t } = useTranslation();
  // 默认不预选：预选首仓会诱导误派（截图误操作反馈），要求派单人显式挑仓
  const [warehouseId, setWarehouseId] = useState<number | undefined>(undefined);
  // 切换片区后可选仓整批换掉，残留的选中项属于上一个 hub，派出去会落到错误的仓；清空而不是回落首项
  useEffect(() => {
    if (warehouseId === undefined || warehouses.some((w) => w.warehouseId === warehouseId)) {
      return;
    }
    setWarehouseId(undefined);
  }, [warehouses, warehouseId]);
  // tz 挂在仓上（非片区上），派单要按选中仓的墙钟算；同 hub 下 LA 与 LA_DC 可以不同。
  // 上游偶发下发浏览器 `Intl` 不认的偏移串（如 `GMT-04:00`），后端已尽力翻成 `Etc/GMT±N`，
  // 但仍要在使用前用 `Intl.DateTimeFormat` 试一下 —— 若拿到无法解析的值，dayjs.tz 底层
  // `Date.toLocaleString` 会直接抛 RangeError 把 DatePicker 整个流程崩掉，页面白屏
  const rawTimeZone = warehouses.find((w) => w.warehouseId === warehouseId)?.timeZone ?? null;
  const timeZone = React.useMemo(() => {
    if (!rawTimeZone) return null;
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: rawTimeZone });
      return rawTimeZone;
    } catch {
      return null;
    }
  }, [rawTimeZone]);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  // 仓「今天」始于该仓时区的零点。dayjs 必须显式 `.tz(timeZone)` 锚在仓上——
  // 否则底层是浏览器本地，选择器上显示的「墙上时钟」是派发人的，不是仓的
  const hubToday = (): Dayjs =>
    (timeZone ? dayjs().tz(timeZone) : dayjs()).startOf('day');
  // 默认次日 18:00 仓本地，是设计稿「次日结束」的常见落点，派发人可直接改
  const [dueAt, setDueAt] = useState<Dayjs | null>(hubToday().add(1, 'day').hour(18));

  // 切换仓可能换 tz（LA → LA_DC 差一个时区），保持墙钟不变、锚到新 tz 重解释。
  // 若不重锚，同一个 `dueAt` 在新仓 tz 下的墙钟会漂 —— 派发人看到的 18:00 传出去就变成别的时刻
  useEffect(() => {
    if (!dueAt || !timeZone) {
      return;
    }
    const wallClock = dueAt.format('YYYY-MM-DD HH:mm:ss');
    const rebased = dayjs.tz(wallClock, timeZone);
    if (rebased.valueOf() !== dueAt.valueOf()) {
      setDueAt(rebased);
    }
    // dueAt 变化时 handleDueChange 已经把新值锚好了，此处再跑一次也是幂等的（rebased === dueAt）；
    // 加入 deps 只是为了满足 hook 规则，不引入额外 setState
  }, [timeZone, dueAt]);

  // 用户在选择器里改时刻，antd 会用浏览器本地 dayjs 建新值——若不重锚，
  // 「04:00」表面看是仓时钟，实际是浏览器时钟，提交时会整体偏移一个时区差
  const handleDueChange = (value: Dayjs | null) => {
    if (!value || !timeZone) {
      setDueAt(value);
      return;
    }
    setDueAt(dayjs.tz(value.format('YYYY-MM-DD HH:mm:ss'), timeZone));
  };
  const [priority, setPriority] = useState('HIGH');
  const [requirePhotoProof, setRequirePhotoProof] = useState(false);
  const [createHubTask, { loading: submitting }] = useMutation(CREATE_HUB_TASK);

  const handleAssign = async () => {
    if (!warehouseId) {
      message.error(String(t('hubReview.assign.warehouseRequired')));
      return;
    }
    if (!title.trim()) {
      message.error(String(t('hubReview.assign.titleRequired')));
      return;
    }
    if (!dueAt) {
      message.error(String(t('hubReview.assign.dueRequired')));
      return;
    }

    try {
      const { data } = await createHubTask({
        variables: {
          hubId,
          warehouseId,
          title: title.trim(),
          detail: detail.trim() || null,
          priority,
          requirePhoto: requirePhotoProof,
          // dueAt 内部已锚在 hub 时区（handleDueChange 每次改动都会重锚），
          // toISOString 直接算出对应的 UTC 时刻
          dueAt: dueAt.toISOString(),
        },
      });
      // 后端返回新建任务 id；为空说明上游没建出来，不能报成功
      if (data?.createHubTask) {
        message.success(String(t('hubReview.assign.assigned')));
        setTitle('');
        setDetail('');
        setRequirePhotoProof(false);
        onAssigned?.();
      } else {
        message.error(String(t('hubReview.assign.assignFailed')));
      }
    } catch (e) {
      message.error(String(t('hubReview.assign.assignFailed')));
    }
  };

  return (
    <Card>
      <h3 className="text-base font-semibold mb-1">
        {String(t('hubReview.assign.title'))}
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        {String(t('hubReview.assign.subtitle'))}
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          {String(t('hubReview.assign.warehouse'))}
        </label>
        <Select
          className="w-full"
          value={warehouseId}
          onChange={setWarehouseId}
          placeholder={String(t('hubReview.assign.warehousePlaceholder'))}
          options={warehouses.map((w) => ({
            value: w.warehouseId,
            label: w.warehouseName,
          }))}
        />
      </div>

      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">
          {String(t('hubReview.assign.task'))}
        </label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={String(t('hubReview.assign.taskPlaceholder'))}
          maxLength={100}
          showCount
        />
      </div>

      {recentTitles.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-y-1">
          <span className="text-xs text-gray-500 mr-2">
            {String(t('hubReview.assign.recentlyUsed'))}
          </span>
          {recentTitles.map((recent) => (
            <Tooltip key={recent} title={recent}>
              <Tag
                className="cursor-pointer max-w-[160px] truncate"
                onClick={() => setTitle(recent)}
              >
                {recent}
              </Tag>
            </Tooltip>
          ))}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          {String(t('hubReview.assign.detail'))}
        </label>
        <Input.TextArea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder={String(t('hubReview.assign.detailPlaceholder'))}
          rows={3}
          maxLength={300}
          showCount
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {String(t('hubReview.assign.due'))}
          </label>
          <DatePicker
            className="w-full"
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={dueAt}
            onChange={handleDueChange}
            // 派发的任务最早次日班次才执行，今天及更早不是有效截止日；
            // 「此刻」会跳回今天，与该约束冲突，一并关掉
            disabledDate={(current) => current.isBefore(hubToday().add(1, 'day'), 'day')}
            showNow={false}
            placeholder={String(t('hubReview.assign.duePlaceholder'))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {String(t('hubReview.assign.priority'))}
          </label>
          {/* 默认皮肤的选中块是白底，与未选中的浅灰几乎无差别，派单人分不清当前选的是「高」还是「普通」；
              选中态改主色底white 字，口径同 AllocateParcelsDrawer 的分段控制器 */}
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: '#722ed1',
                  itemSelectedColor: '#FFFFFF',
                },
              },
            }}
          >
            <Segmented
              block
              value={priority}
              onChange={(value) => setPriority(String(value))}
              options={[
                { value: 'HIGH', label: String(t('hubReview.assign.priorityHigh')) },
                { value: 'NORMAL', label: String(t('hubReview.assign.priorityNormal')) },
              ]}
            />
          </ConfigProvider>
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="primary" onClick={handleAssign} loading={submitting}>
          {String(t('hubReview.assign.submit'))}
        </Button>
        <Button
          onClick={() => setRequirePhotoProof(!requirePhotoProof)}
          className={requirePhotoProof ? 'border-violet-600 text-violet-600' : undefined}
        >
          {String(t('hubReview.assign.requirePhotoProof'))}
        </Button>
      </div>
    </Card>
  );
};

export default AssignTaskPanel;
